import { useMemo } from 'react'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import { useLabels as useRxDBLabels } from '@/lib/db/rxdb/hooks'

/**
 * Hook for fetching labels via RxDB.
 * Supports both workspace-level and project-level labels.
 *
 * @param workspaceId - Current workspace ID
 * @param projectId - Optional project ID. If null/undefined, returns workspace-level labels
 *
 * @example
 * // Get workspace-level labels
 * const { labels } = useLabels(workspaceId)
 *
 * @example
 * // Get project-specific labels
 * const { labels } = useLabels(workspaceId, projectId)
 */
export function useLabels(
  workspaceId: string | null | undefined,
  projectId?: string | null | undefined,
) {
  // Get all labels from RxDB
  const { labels: allLabels, isLoading } = useRxDBLabels()

  // Filter labels based on workspace and project
  const labels = useMemo(() => {
    if (!workspaceId) {
      return []
    }

    return allLabels
      .filter((label: LabelDocument) => {
        // Must match workspace
        if (label.envId !== workspaceId) {
          return false
        }

        // If projectId is provided, match project-specific labels
        // If projectId is null/undefined, match workspace-level labels (projectId is null)
        if (projectId !== undefined) {
          return (label as any).projectId === projectId
        } else {
          return !(label as any).projectId
        }
      })
      .sort((a: LabelDocument, b: LabelDocument) => {
        // Sort by sortOrder if available, otherwise by name
        const aOrder = (a as any).sortOrder
        const bOrder = (b as any).sortOrder
        if (aOrder && bOrder) {
          return aOrder.localeCompare(bOrder)
        }
        return a.name.localeCompare(b.name)
      })
  }, [workspaceId, projectId, allLabels])

  return {
    labels,
    isLoading,
  }
}

/**
 * Hook for fetching all labels (both workspace-level and project-specific)
 *
 * @param workspaceId - Current workspace ID
 *
 * @example
 * const { labels } = useAllLabels(workspaceId)
 */
export function useAllLabels(
  workspaceId: string | null | undefined,
) {
  // Get all labels from RxDB
  const { labels: allLabels, isLoading } = useRxDBLabels()

  const labels = useMemo(() => {
    if (!workspaceId) {
      return []
    }

    return allLabels
      .filter(
        (label: LabelDocument) =>
          label.envId === workspaceId,
      )
      .sort((a: LabelDocument, b: LabelDocument) => {
        // Sort by sortOrder if available, otherwise by name
        const aOrder = (a as any).sortOrder
        const bOrder = (b as any).sortOrder
        if (aOrder && bOrder) {
          return aOrder.localeCompare(bOrder)
        }
        return a.name.localeCompare(b.name)
      })
  }, [workspaceId, allLabels])

  return {
    labels,
    isLoading,
  }
}
